import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, printPdf } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHeader, EmptyState, StatusBadge } from "@/components/common/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Pagination, usePagination } from "@/components/common/Pagination";
import { Bed, Plus, MagnifyingGlass, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function IPDPage() {
  const [tab, setTab] = useState("admitted");
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  useEffect(() => {
    api.get("/admissions", { params: { status: tab } }).then((r) => setList(r.data || []));
  }, [tab]);

  // Client-side filter — matches IPD No., MRN, patient name, doctor, ward, bed.
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return list;
    return list.filter((a) => {
      const hay = [
        a.admission_no, a.mrn, a.patient_name, a.doctor, a.ward, a.bed, a.diagnosis,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [list, q]);

  const { page, setPage, pageSize, setPageSize, paged, total, pageCount } = usePagination(filtered, 25);

  return (
    <div data-testid="ipd-page">
      <PageHeader eyebrow="IPD" title="In-Patient Admissions"
        subtitle="Live census of admitted patients and history of discharges. Click any row to manage progress, billing, and discharge."
        actions={
          <>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--hms-muted)]" />
              <Input
                placeholder="Search IPD No., MRN, name, doctor, ward…"
                value={q} onChange={(e) => setQ(e.target.value)}
                className="pl-8 w-64 rounded-sm border-[var(--hms-border-2)] bg-white"
                data-testid="ipd-search"
              />
            </div>
            <Button
              onClick={() => nav("/patients?register=1")}
              data-testid="ipd-register-patient"
              className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]"
            >
              <Plus size={14} weight="bold" className="mr-1.5" /> Register patient
            </Button>
          </>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-3">
        <TabsList className="bg-[var(--hms-paper)] rounded-sm border border-[var(--hms-border-2)]">
          <TabsTrigger value="admitted" data-testid="ipd-tab-admitted" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-none">Admitted</TabsTrigger>
          <TabsTrigger value="discharged" data-testid="ipd-tab-discharged" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-none">Discharged</TabsTrigger>
        </TabsList>
      </Tabs>

      {list.length === 0 ? (
        <EmptyState title={`No ${tab} admissions`} hint="Admissions will show up here." />
      ) : filtered.length === 0 ? (
        <EmptyState title={`No matches for "${q}"`} hint="Try a different IPD No., MRN, name, or doctor." />
      ) : (
        <div className="hms-card overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--hms-paper)] text-[10px] uppercase tracking-wider font-bold text-[var(--hms-text-2)] border-b border-[var(--hms-border-2)]">
              <tr>
                <th className="text-left px-4 py-2.5">IPD No.</th>
                <th className="text-left px-4 py-2.5">Patient Name</th>
                <th className="text-left px-4 py-2.5">MRN</th>
                <th className="text-left px-4 py-2.5">Admit Date</th>
                <th className="text-left px-4 py-2.5">Ward / Bed</th>
                <th className="text-left px-4 py-2.5">Status</th>
                {tab === "discharged" && <th className="text-left px-4 py-2.5">Discharge Type</th>}
                <th className="text-right px-4 py-2.5"></th>
                <th className="text-right px-4 py-2.5">Print</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((a) => (
                <tr key={a.id} className="border-b border-[var(--hms-border)] hover:bg-[var(--hms-paper)]/50" data-testid={`adm-row-${a.admission_no}`}>
                  <td className="px-4 py-2 font-mono text-xs">
                    <Link to={`/admissions/${a.id}`} className="font-semibold hover:text-[var(--hms-accent)]">{a.admission_no}</Link>
                  </td>
                  <td className="px-4 py-2 font-semibold">
                    <Link to={`/patients/${a.mrn}`} className="hover:text-[var(--hms-accent)]">{a.patient_name || "—"}</Link>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">
                    <Link to={`/patients/${a.mrn}`} className="hover:text-[var(--hms-accent)]">{a.mrn}</Link>
                  </td>
                  <td className="px-4 py-2 hms-tabular">{fmtDate(a.admit_date)}</td>
                  <td className="px-4 py-2"><Bed size={12} className="inline mr-1.5 text-[var(--hms-muted)]" />{a.ward} / {a.bed}</td>
                  <td className="px-4 py-2"><StatusBadge status={a.status} /></td>
                  {tab === "discharged" && (
                    <td className="px-4 py-2">
                      <DischargeTypeBadge type={a.discharge_type} />
                    </td>
                  )}
                  <td className="px-4 py-2 text-right">
                    <Link to={`/admissions/${a.id}`} className="text-xs font-bold text-[var(--hms-accent)] hover:underline">Open</Link>
                  </td>
                  <td className="px-4 py-2 text-right">
                    {a.status === "discharged" ? (
                      <button
                        onClick={() => printPdf(`/pdf/discharge/${a.id}`).catch((e) => toast.error(e.message || "Print failed"))}
                        data-testid={`ipd-discharge-print-${a.admission_no}`}
                        title="Print discharge card"
                        aria-label="Print discharge card"
                        className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-[var(--hms-text-2)] hover:text-[var(--hms-accent)] hover:bg-[var(--hms-hover)] cursor-pointer transition-colors"
                      >
                        <Printer size={16} weight="bold" />
                      </button>
                    ) : (
                      <button
                        onClick={() => printPdf(`/pdf/admit-card/${a.id}`).catch((e) => toast.error(e.message || "Print failed"))}
                        data-testid={`ipd-admit-print-${a.admission_no}`}
                        title="Print admit card"
                        aria-label="Print admit card"
                        className="inline-flex items-center justify-center h-7 w-7 rounded-sm text-[var(--hms-text-2)] hover:text-[var(--hms-accent)] hover:bg-[var(--hms-hover)] cursor-pointer transition-colors"
                      >
                        <Printer size={16} weight="bold" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} setPage={setPage} pageSize={pageSize}
            setPageSize={setPageSize} total={total} pageCount={pageCount} testidPrefix="ipd" />
        </div>
      )}
    </div>
  );
}

function DischargeTypeBadge({ type }) {
  const t = type || "Normal";
  const map = {
    Normal: "bg-[#588157]/15 text-[#588157] border-[#588157]/30",
    DAMA:   "bg-[#E08E45]/15 text-[#9A5B12] border-[#E08E45]/40",
    Death:  "bg-[#C8553D]/15 text-[#C8553D] border-[#C8553D]/30",
  };
  const cls = map[t] || map.Normal;
  return (
    <span
      data-testid={`ipd-discharge-type-${t.toLowerCase()}`}
      className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest border ${cls}`}
      title={`Discharge type: ${t}`}
    >
      {t}
    </span>
  );
}
